import { useState } from "react";
import { MessageCircle, Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "../../lib/site";

const UNITS = [
  { value: "", label: "Selecione uma unidade" },
  { value: "infra", label: "Infraestrutura" },
  { value: "nexus-internet", label: "Nexus Internet" },
  { value: "vision", label: "Vision (Monitoramento)" },
  { value: "monal", label: "Monal (Inovação)" },
];

const UNIT_LABEL = {
  "infra": "Infraestrutura",
  "nexus-internet": "Nexus Internet",
  "vision": "Vision",
  "monal": "Monal",
};

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
    unit: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      // Build a clean WhatsApp message from the form
      const lines = [
        "Olá, Grupo Nexus! Vim pelo site:",
        "",
        `Nome: ${form.name}`,
        `E-mail: ${form.email}`,
      ];
      if (form.phone) lines.push(`Telefone: ${form.phone}`);
      if (form.company) lines.push(`Empresa: ${form.company}`);
      if (form.unit && UNIT_LABEL[form.unit]) lines.push(`Unidade: ${UNIT_LABEL[form.unit]}`);
      lines.push(`Assunto: ${form.subject}`);
      lines.push("");
      lines.push("Mensagem:");
      lines.push(form.message);

      const text = encodeURIComponent(lines.join("\n"));
      const url = `https://wa.me/${SITE.phoneRaw}?text=${text}`;

      // Open WhatsApp in a new tab
      window.open(url, "_blank", "noopener,noreferrer");

      toast.success("Mensagem pronta no WhatsApp!", {
        description: "Conclua o envio na conversa que abrimos.",
      });

      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "", unit: "" });
    } catch (err) {
      toast.error("Não foi possível abrir o WhatsApp", {
        description: "Tente novamente ou fale direto pelo botão acima.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" data-testid="contact-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        {/* Final CTA card */}
        <div
          className="nx-rainbow-border relative overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-[#12093a] via-[#0A0810] to-black p-10 md:p-16"
          data-testid="final-cta-card"
          data-active="true"
        >
          <div className="nx-blob bg-[#4c2e9b] w-[540px] h-[540px] -top-40 -left-32" aria-hidden />
          <div className="nx-blob bg-[#00f0ff]/30 w-[360px] h-[360px] -bottom-24 -right-10" aria-hidden />
          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="overline">Vamos conversar</span>
              <h2 className="font-display mt-5 text-3xl md:text-5xl font-medium tracking-tight text-white leading-[1.05]">
                Precisa de uma solução tecnológica{" "}
                <span className="nx-rainbow-text">à altura</span> do seu projeto?
              </h2>
              <p className="mt-6 text-[15px] md:text-base text-[#b3b0c2] max-w-xl leading-relaxed">
                O Grupo Nexus está pronto para conectar, estruturar e proteger sua operação com
                soluções completas e atendimento próximo.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={SITE.whatsappUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="final-cta-whatsapp"
                  className="nx-btn-primary"
                >
                  <MessageCircle size={16} /> Falar no WhatsApp
                </a>
                <a href="#contact-form" data-testid="final-cta-contact" className="nx-btn-secondary">
                  Enviar mensagem <Send size={14} />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-3">
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email-link"
                className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur px-5 py-4 hover:bg-white/[0.06] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <Mail size={16} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">E-mail</div>
                    <div className="text-sm text-white">{SITE.email}</div>
                  </div>
                </div>
              </a>
              <a
                href={`tel:+${SITE.phoneRaw}`}
                data-testid="contact-phone-link"
                className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur px-5 py-4 hover:bg-white/[0.06] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <Phone size={16} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">Telefone</div>
                    <div className="text-sm text-white">{SITE.phoneLabel}</div>
                  </div>
                </div>
              </a>
              <a
                href={SITE.mapsUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="contact-maps-link"
                className="block rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur overflow-hidden hover:bg-white/[0.06] transition-colors group"
              >
                <div className="relative h-[180px] overflow-hidden">
                  <iframe
                    src={SITE.mapsEmbedSrc}
                    title="Localização Grupo Nexus"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="absolute inset-0 w-full h-full border-0"
                    style={{ filter: "grayscale(0.25) contrast(1.05) brightness(0.85)" }}
                    allowFullScreen
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>
                <div className="flex items-center gap-3 px-5 py-4">
                  <span className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <MapPin size={16} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">
                      Endereço
                    </div>
                    <div className="text-sm text-white truncate group-hover:underline">
                      Abrir no Google Maps
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Contact form */}
        <div id="contact-form" className="mt-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="overline">Formulário</span>
            <h3 className="font-display mt-4 text-2xl md:text-3xl font-medium text-white leading-snug">
              Conte o seu projeto.{" "}
              <span className="text-white/60">Enviaremos sua mensagem direto ao WhatsApp da equipe.</span>
            </h3>
            <p className="mt-5 text-[14.5px] text-[#a19eb0] leading-relaxed">
              Preencha o formulário e ao enviar uma conversa será aberta no WhatsApp com todos os
              dados já preenchidos. Rápido, prático e sem cadastros.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 rounded-[24px] border border-white/10 bg-[#08070d] p-6 md:p-8 space-y-4"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nome completo*" required>
                <input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={update("name")}
                  required
                  minLength={2}
                  className={inputCx}
                  placeholder="Seu nome"
                />
              </Field>
              <Field label="E-mail*" required>
                <input
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={update("email")}
                  type="email"
                  required
                  className={inputCx}
                  placeholder="voce@empresa.com"
                />
              </Field>
              <Field label="Telefone / WhatsApp">
                <input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  className={inputCx}
                  placeholder="(DDD) 00000-0000"
                />
              </Field>
              <Field label="Empresa (opcional)">
                <input
                  data-testid="contact-input-company"
                  value={form.company}
                  onChange={update("company")}
                  className={inputCx}
                  placeholder="Nome da empresa"
                />
              </Field>
              <Field label="Unidade de interesse">
                <select
                  data-testid="contact-select-unit"
                  value={form.unit}
                  onChange={update("unit")}
                  className={inputCx}
                >
                  {UNITS.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Assunto*" required>
                <input
                  data-testid="contact-input-subject"
                  value={form.subject}
                  onChange={update("subject")}
                  required
                  minLength={2}
                  className={inputCx}
                  placeholder="Ex.: Projeto de CFTV para condomínio"
                />
              </Field>
            </div>
            <Field label="Mensagem*" required>
              <textarea
                data-testid="contact-textarea-message"
                value={form.message}
                onChange={update("message")}
                required
                minLength={5}
                rows={5}
                className={`${inputCx} resize-none`}
                placeholder="Conte-nos sobre seu projeto, tamanho, prazo e expectativas."
              />
            </Field>
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-[12px] text-white/45">
                Ao enviar, abriremos uma conversa pré-preenchida no WhatsApp.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="nx-btn-primary disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Abrindo…
                  </>
                ) : (
                  <>
                    <MessageCircle size={14} /> Enviar via WhatsApp
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const inputCx =
  "w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-[14.5px] text-white placeholder:text-white/35 focus:outline-none focus:border-white/30 focus:bg-white/[0.05] transition-colors";

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[11.5px] uppercase tracking-[0.18em] text-white/55">{label}</span>
    {children}
  </label>
);

export default Contact;
